import React, { useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { LoginService, OpenAPI } from "@propertelligent/client-api";
import { TopBar } from "../common/TopBar";
import "./SignupStyles.css";
import OtpInput from "./OtpInput";
import { GetImages } from "../../utils/GetImages";
import { UserLoginData } from "../common/charts/Enums";
import { URLS, USER_ROLES, USERS } from "../../constants/constants";
import { MyContext } from "../../utils/context/LoginContext";
import apiHelper from "../../utils/apiHelper";         // Reusing
import AddButtonWithArrow from "../firstTimeUserExperience/commonCom/AddButtonWithArrow";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import InputField from "../../newComponents/form-fields/InputField";
import PhoneInput from "../../newComponents/form-fields/PhoneInput";
import EmailInput from "../../newComponents/form-fields/EmailInput";
import MobileAuthLink from "./MobileAuthLink";
import { usePermissions } from "../../hooks/usePermissions";

interface SignupFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  password: string;
  organizationName: string;
}

const Signup = () => {
  document.title = "Signup | Propertelligent";

  const { setIsLogin } = useContext(MyContext);
  const { refreshPermissions } = usePermissions();
  const navigate = useNavigate();

  const [loading, setLoading] = React.useState(false);
  const [signedIn, setSignedIn] = React.useState(false);
  const [codeError, setCodeError] = React.useState(false);
  const [code, setCode] = React.useState("");

  const methods = useForm<SignupFormValues>({
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      email: "",
      password: "",
      organizationName: "",
    },
  });

  const { handleSubmit, watch, setError, clearErrors } = methods;
  const values = watch(); // To get current form values (for OTP step)

  // OTP Verification
  const handleCodeInputChange = async (value: string) => {
    setCodeError(false);
    setCode(value);

    if (value.length === 6) {
      setLoading(true);
      try {
        const response = await apiHelper(
          LoginService.putApiLoginVerifyEmail,
          { showNotification: false },
          { email: values.email, code: Number(value) }
        );

        if (response.data.success) {
          const data = response.data.success;
          sessionStorage.setItem(UserLoginData.token, data.token);
          OpenAPI.TOKEN = data.token;
          sessionStorage.setItem(UserLoginData.user_name, `${data.firstName} ${data.lastName}`);
          sessionStorage.setItem(UserLoginData.user_id.toString(), data.userId);
          sessionStorage.setItem("actualUserId", data.userId?.toString() || "");
          sessionStorage.setItem(UserLoginData.organization_id, data.organizationUserInfo.organizationId);
          sessionStorage.setItem(UserLoginData.currency_symbol, data.currencySymbol);
          sessionStorage.setItem("userOnboarded", data.userOnboarded);
          sessionStorage.setItem("userRoleId", data.userRoleId);
          sessionStorage.setItem(
            UserLoginData.permissions,
            JSON.stringify(data.organizationUserInfo?.permissions || {})
          );
          refreshPermissions();

          if (USER_ROLES[data.userRoleId] === USERS.TENANT) {
            sessionStorage.setItem("tenantInfo", JSON.stringify(data.tenantInfo));
          } else if (USER_ROLES[data.userRoleId] === USERS.CONTRACTOR) {
            // ?? null: same JSON.stringify(undefined) -> invalid "undefined" string -> JSON.parse
            // crash risk as login.service.ts if contractorInfo is ever missing here.
            sessionStorage.setItem("contractorInfo", JSON.stringify(data.contractorInfo ?? null));
          }

          setIsLogin(true);
          navigate(data.userOnboarded ? URLS.HOME : URLS.HOME); // Adjust as needed
        }
      } catch {
        setCodeError(true);
      } finally {
        setLoading(false);
      }
    }
  };

  // Signup Submit
  const onSubmit = async (data: SignupFormValues) => {
    setLoading(true);
    try {
      const response = await apiHelper(
        LoginService.postApiLoginSignup,
        { showNotification: false },
        data
      );

      if (response?.data?.success) {
        setSignedIn(true);
      } else if (response?.errors?.Email) {
        setError("email", { message: response.errors.Email[0] });
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: response.errors.Email[0],
          type: NotificationType.Failure,
        });
      }
    } catch (error: any) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Something went wrong. Please try again.",
        type: NotificationType.Failure,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = () => {
    apiHelper(
      LoginService.putApiLoginResendEmailVerificationCode,
      {
        showNotification: true,
        successMessage: "Verification code sent again",
        failureMessage: "Failed to send code",
      },
      values.email
    );
  };

  return (
    <div
      className="background-section background-image signup-page-container"
      style={{ paddingBottom: "0px" }}
    >
      <TopBar haveAccount="Have an account?" signInUp="Log in" />

      <div className="mainContainer">
        {/* Left Side Content */}
        <div className="leftSideMainContainer">
          <div className="leftSideContainerContent">
            <p className="leftSideTitleText top_header">What sets Propertelligent apart</p>
            <div className="textBox">
              <div className="smallLeftImage"> <img src={GetImages.smart} /></div>
              <div>
                <p className="main_heading navyBlue">Smart Rental Applications</p>
                <p className="boxDescription normal_text fw-500">
                  Easily screen the right tenant for your properties with the
                  information that is most important to you.
                </p>
              </div>
            </div>
            <div className="textBox">
              <div className="smallLeftImage"> <img src={GetImages.finance} /> </div>
              <div>
                <p className="main_heading navyBlue">Finance Tools Just for Landlords</p>
                <p className="boxDescription normal_text fw-500">
                  Optimize your finances with industry-leading accounting and
                  tax prep tools built for landlords.
                </p>
              </div>
            </div>
            <div className="textBox">
              <div className="smallLeftImage"> <img src={GetImages.simplified} /> </div>
              <div>
                <p className="main_heading navyBlue">Simplified Rent Collection</p>
                <p className="boxDescription normal_text fw-500">
                  Easily screen the right tenant for your properties with the
                  information that is most important to you.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Form */}
        <div className="rightSideFormContainer">
          {signedIn ? (
            /* OTP Screen */
            <div className="emailVerifyMainBox">
              <p className="top_header black mb-2">Enter email verification code</p>
              <p className="normal_text grey_subtext1 mb-4">
                A 6-digit code was sent to <span className="emailSpan ms-1">{values.email}</span>
              </p>

              <div style={{ marginBottom: codeError ? "8px" : "32px" }}>
                <OtpInput
                  handleCodeInputChange={handleCodeInputChange}
                  code={code}
                  setCode={setCode}
                  status={signedIn}
                  comingFrom="signup"
                />
              </div>

              {codeError && <p className="sub_text red mb-4">Incorrect code. Please try again</p>}

              <p className="normal_text grey_subtext1 fw-500">
                Didn’t receive it?{" "}
                <span className="resendCodeText normal_text blue" onClick={handleResendCode}>
                  Resend code
                </span>
              </p>
            </div>
          ) : (
            /* Signup Form */
            <div className="formContainer">
              <p className="signInFormTitle top_header">Create an account</p>

              <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="row g-3">
                    {/* First Name & Last Name */}
                    <div className="col-md-6">
                      <InputField
                        name="firstName"
                        label="First Name"
                        required
                        requiredMessage="First Name is required"
                        placeholder="First Name"
                        onlyAlphabets={true}
                        validation={{
                          minLength: { value: 2, message: "First name must be at least 2 characters long" },
                          maxLength: { value: 20, message: "First name cannot exceed 20 characters" },
                        }}
                      />
                    </div>
                    <div className="col-md-6">
                      <InputField
                        name="lastName"
                        label="Last Name"
                        required
                        requiredMessage="Last Name is required"
                        placeholder="Last Name"
                        onlyAlphabets={true}
                        validation={{
                          minLength: { value: 2, message: "Last name must be at least 2 characters long" },
                          maxLength: { value: 20, message: "Last name cannot exceed 20 characters" },
                        }}  
                      />
                    </div>

                    {/* Email */}
                    <div className="col-md-6">
                      <EmailInput
                        name="email"
                        label="Email Address"
                        required
                        requiredMessage="Email is required"
                        placeholder="Enter Email Address"
                        
                      />
                    </div>

                    {/* Phone Number */}
                    <div className="col-md-6">
                      <PhoneInput
                        name="phoneNumber"
                        label="Contact No"
                        required
                        requiredMessage="Contact No is required"
                      />

                    </div>

                    {/* Organization Name */}
                    <div className="col-md-6">
                      <InputField
                        name="organizationName"
                        label="Organization Name"
                        required
                        requiredMessage="Organization Name is required"
                        placeholder="Enter Organization Name"
                         validation={{
                          minLength: { value: 2, message: "Must be 2 character" },
                          maxLength: { value: 40, message: "Must be 40 characters" },
                        }}
                      />
                    </div>

                    {/* Password */}
                    <div className="col-md-6">
                      <InputField
                        name="password"
                        label="Password"
                        type="password"
                        required
                        requiredMessage="Password is required"
                        placeholder="Enter Password"
                        allowSpecialChars
                        validation={{
                          minLength: { value: 8, message: "Password must be at least 8 characters long" },
                          maxLength: { value: 20, message: "Password cannot exceed 20 characters" },
                          pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,20}$/,
                            message: "Password must contain at least one lowercase letter, one uppercase letter, one number, one special character, and be between 8 and 20 characters long"
                          },
                          notCommon: {
                            value: /^(?!.*(password|123456|qwerty|letmein|welcome)).*$/,
                            message: "Password should not be a common word or sequence"
                          }
                        }}
                      />

                    </div>
                  </div>

                  <div className="mt-4">
                    <AddButtonWithArrow
                      className="signUpButton sub_heading w-100"
                      type="submit"
                      buttonname="Sign Up"
                      disabled={loading}
                      loading={loading}
                    />
                  </div>

                  {/* Mobile Only Login Link */}
                  <MobileAuthLink variant="login" />
                </form>
              </FormProvider>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Signup;